import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';
import { DealReportStatus } from './deal';
import { AuthService } from '../auth/auth';

/**
 * Минимальный STOMP-клиент поверх нативного WebSocket (без sockjs).
 * Подписывается на /topic/deal-report/{dealId} — тот же паттерн, что и InvoiceSocketService.
 */
@Injectable({ providedIn: 'root' })
export class DealReportSocketService {
  constructor(private zone: NgZone, private authService: AuthService) {}

  watch(dealId: number): Observable<DealReportStatus> {
    return new Observable(subscriber => {
      if (typeof window === 'undefined' || typeof WebSocket === 'undefined') {
        subscriber.complete();
        return;
      }

      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url = `${proto}//${window.location.host}/cargo/ws`;
      let socket: WebSocket | null = null;
      let closed = false;
      let subId = 'sub-deal-report';

      const sendFrame = (command: string, headers: Record<string, string> = {}, body = '') => {
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        const lines = [command, ...Object.entries(headers).map(([k, v]) => `${k}:${v}`), '', body];
        socket.send(lines.join('\n') + '\0');
      };

      try {
        socket = new WebSocket(url);
      } catch (e) {
        subscriber.error(e);
        return;
      }

      socket.onopen = () => {
        const token = this.authService.getToken();
        const connectHeaders: Record<string, string> = { 'accept-version': '1.2,1.1,1.0', 'heart-beat': '0,0' };
        if (token) connectHeaders['Authorization'] = `Bearer ${token}`;
        sendFrame('CONNECT', connectHeaders);
      };

      socket.onmessage = (event) => {
        const frames = String(event.data).split('\0').filter(Boolean);
        for (const raw of frames) {
          const parsed = parseStomp(raw);
          if (!parsed) continue;
          if (parsed.command === 'CONNECTED') {
            sendFrame('SUBSCRIBE', {
              id: subId,
              destination: `/topic/deal-report/${dealId}`
            });
          } else if (parsed.command === 'MESSAGE' && parsed.body) {
            try {
              const payload = JSON.parse(parsed.body) as DealReportStatus;
              this.zone.run(() => subscriber.next(payload));
            } catch {
              // ignore malformed frames
            }
          } else if (parsed.command === 'ERROR') {
            this.zone.run(() => subscriber.error(new Error(parsed.body || 'STOMP error')));
          }
        }
      };

      socket.onerror = () => {
        this.zone.run(() => subscriber.error(new Error('WebSocket error')));
      };

      socket.onclose = () => {
        if (!closed) {
          this.zone.run(() => subscriber.complete());
        }
      };

      return () => {
        closed = true;
        try {
          sendFrame('UNSUBSCRIBE', { id: subId });
          sendFrame('DISCONNECT');
        } catch {
          // ignore
        }
        socket?.close();
      };
    });
  }
}

function parseStomp(raw: string): { command: string; body: string } | null {
  const trimmed = raw.replace(/^\n+/, '');
  if (!trimmed) return null;
  const headerEnd = trimmed.indexOf('\n\n');
  const headerPart = headerEnd >= 0 ? trimmed.slice(0, headerEnd) : trimmed;
  const body = headerEnd >= 0 ? trimmed.slice(headerEnd + 2) : '';
  const command = headerPart.split('\n')[0]?.trim();
  if (!command) return null;
  return { command, body };
}
